import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  TrophyIcon,
  DocumentTextIcon,
  CalendarIcon,
  BookOpenIcon,
  PhotoIcon,
  HeartIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import searchService, { searchCategories, popularSearches } from '../../services/searchService';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Icon mapping
  const iconMap = {
    MagnifyingGlassIcon,
    TrophyIcon,
    DocumentTextIcon,
    CalendarIcon,
    BookOpenIcon,
    PhotoIcon,
    HeartIcon
  };

  // Load initial data
  useEffect(() => {
    setRecentSearches(searchService.getRecentSearches());
    
    // Perform search if query exists in URL
    const urlQuery = searchParams.get('q');
    const urlCategory = searchParams.get('category');
    
    if (urlQuery) {
      setQuery(urlQuery);
      setSelectedCategory(urlCategory || 'all');
      performSearch(urlQuery, urlCategory || 'all');
    }
  }, [searchParams]);

  // Perform search
  const performSearch = async (searchQuery = query, category = selectedCategory) => {
    if (!searchQuery.trim()) return;
    
    setIsLoading(true);
    setHasSearched(true);
    
    try {
      const searchResults = await searchService.search(searchQuery, {
        category,
        limit: 50
      });
      
      setResults(searchResults.results.map(result => 
        searchService.formatSearchResult(result)
      ));
      
      // Save to recent searches
      searchService.saveRecentSearch(searchQuery);
      setRecentSearches(searchService.getRecentSearches());
      
      // Update URL
      const params = new URLSearchParams();
      params.set('q', searchQuery);
      if (category !== 'all') {
        params.set('category', category);
      }
      setSearchParams(params);
      
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    performSearch();
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (query.trim()) {
      performSearch(query, category);
    }
  };

  // Handle popular search click
  const handlePopularSearchClick = (popularQuery) => {
    setQuery(popularQuery);
    performSearch(popularQuery, selectedCategory);
  };

  // Handle recent search click
  const handleRecentSearchClick = (recentQuery) => {
    setQuery(recentQuery);
    performSearch(recentQuery, selectedCategory);
  };

  // Clear recent searches
  const handleClearRecent = () => {
    searchService.clearRecentSearches();
    setRecentSearches([]);
  };

  // Get icon component
  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <MagnifyingGlassIcon className="h-5 w-5" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Search</h1>
            <p className="text-lg text-gray-600">Find sports, news, events, and resources</p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative max-w-2xl mx-auto">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search sports, news, events, and more..."
                className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Search
              </button>
            </div>
          </form>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            {searchCategories.map((category) => {
              const IconComponent = iconMap[category.icon];
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {IconComponent && <IconComponent className="h-4 w-4" />}
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* Filter Toggle */}
          <div className="flex justify-center">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <FunnelIcon className="h-4 w-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Search Results */}
        {!isLoading && hasSearched && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {results.length > 0 ? (
                  `${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`
                ) : (
                  `No results for "${query}"`
                )}
              </h2>
              {selectedCategory !== 'all' && (
                <button
                  onClick={() => handleCategoryChange('all')}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Clear filter
                </button>
              )}
            </div>

            {results.length > 0 ? (
              <div className="space-y-4">
                {results.map((result) => (
                  <Link
                    key={`${result.type}-${result.id}`}
                    to={result.url}
                    className="block bg-white p-6 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${result.categoryColor}`}>
                            {result.category}
                          </span>
                          {result.formattedDate && (
                            <span className="text-sm text-gray-500">
                              {result.formattedDate}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{result.title}</h3>
                        <p className="text-gray-600">{result.description}</p>
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-gray-400 ml-4 flex-shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <MagnifyingGlassIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your search terms or browse our popular searches below.</p>
              </div>
            )}
          </div>
        )}

        {/* Default State - Popular and Recent Searches */}
        {!hasSearched && !isLoading && (
          <div className="space-y-8">
            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <div className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <ClockIcon className="h-5 w-5" />
                    Recent Searches
                  </h3>
                  <button
                    onClick={handleClearRecent}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentSearches.map((recentQuery, index) => (
                    <button
                      key={index}
                      onClick={() => handleRecentSearchClick(recentQuery)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      {recentQuery}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Popular Searches */}
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5" />
                Popular Searches
              </h3>
              <div className="flex flex-wrap gap-2">
                {popularSearches.map((popularQuery, index) => (
                  <button
                    key={index}
                    onClick={() => handlePopularSearchClick(popularQuery)}
                    className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                  >
                    {popularQuery}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;